import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useFetch } from "../../../utils/hooks/useFetch";
import { getFileGroupingDetailsByUrl } from "../../../utils/helpers";
import { getProjectTaskServiceConfig } from "../../../utils/services/projects";
import { getHolidaysListForCurrentWeekServiceConfig } from "../../../utils/services/Holidays";
import {
  getComparator,
  getCurrentWeek,
  getYearFormatter,
} from "../../../utils/helpers";
import { translate } from "../../../utils/translations/translate";
import {
  unWindData,
  generateWeekDaysInfo,
  mapResponseDataToUi,
  formatTimeEntriesForReports,
} from "../../../utils/helpers";
import {
  saveOrSubmitTimeSheetRequestConfig,
  getTmSheetSavedInfo,
  updateTimeSheetRequestConfig,
  getFormattedTimeEntriesData,
  deleteTimesheetAttachmentConfig,
} from "../../../utils/services/timesheet";
import { TIMESHEET_ACTION_STATES, TOAST_TIMER } from "../../../utils/constants";
import { startOfWeek, endOfWeek, subWeeks, subDays, addDays } from "date-fns";
import {
  exportTimesheetToPDF,
  generateTimesheetExcel,
} from "../../../utils/reports";
import { useUpdateCount } from "../../Header/updateCount";
export const useTimesheetActions = () => {
  const currentWeek = useMemo(() => {
    return getCurrentWeek();
  }, []);
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);

  const handleWeekChange = useCallback((week) => {
    setSelectedWeek((prevWeek) => {
      if (
        getYearFormatter(week.firstDay) === getYearFormatter(prevWeek.firstDay)
      ) {
        return prevWeek;
      }
      return week;
    });
  }, []);
  const { isError, isLoading, performAction, hideServiceError } = useFetch();
  const [showDraftAction, setShowDraftAction] = useState(false);
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [projectsList, setProjectsList] = useState([]);
  const projectTasksCache = useRef();
  const [holidaysList, setHolidaysList] = useState([]);
  const [currentWeekHolidays, setCurrentWeekHolidays] = useState([]);
  const [projectTasksList, setProjectTasksList] = useState([]);
  const timesheetDetails = useRef({});
  const updateCount = useUpdateCount();
  const [toastInfo, setToastInfo] = useState(null);
  const { autoExpireToast } = toastInfo || {};
  const weekDaysArr = useMemo(() => {
    return generateWeekDaysInfo(
      selectedWeek.firstDay,
      selectedWeek.lastDay,
      currentWeekHolidays
    );
  }, [selectedWeek, currentWeekHolidays]);
  const fetchTimesheetDetailsByWeek = useCallback(
    async ({ firstDay, lastDay }) => {
      try {
        const {
          url: timesheetSavedInfoURL,
          options: timesheetSavedInfOptions,
        } = getTmSheetSavedInfo({ startDate: firstDay, endDate: lastDay });
        const responses = await Promise.allSettled([
          performAction(timesheetSavedInfoURL, timesheetSavedInfOptions),
        ]);
        const [metaInfo] = responses;
        if (
          metaInfo.status === "fulfilled" &&
          Array.isArray(metaInfo.value?.Result?.Projects) &&
          metaInfo.value?.Result?.Projects.length > 0
        ) {
          const formattedProjectList = metaInfo.value?.Result?.Projects.map(
            (prj) => {
              const {
                ProjectID,
                ProjectName,
                Data,
                Comments,
                ApproverComments,
                ManagerName,
                ManagerEmail,
                TimesheetID,
                Status,
                ResourceName,
                RecruiterName,
                ClientName,
                ClientAddress,
                LastApprovedOn,
                LastSubmittedOn,
                Attachments,
              } = prj;
              return {
                attachments: Attachments
                  ? Attachments.split(",").map((attachment) => {
                      return getFileGroupingDetailsByUrl(attachment);
                    })
                  : [],
                projectId: ProjectID,
                projectName: ProjectName,
                managerName: ManagerName,
                managerEmail: ManagerEmail,
                timesheetId: TimesheetID,
                resourceName: ResourceName,
                hiringManager: RecruiterName || "N/A",
                poStartDate: prj.POStartDate ? prj.POStartDate : "",
                poEndDate: prj.POEndDate ? prj.POEndDate : "",
                clientName: ClientName || "N/A",
                workLocation: ClientAddress || "N/A",
                lastApprovedOn: (LastApprovedOn || "").startsWith("0001")
                  ? "N/A"
                  : LastApprovedOn,
                lastSubmittedOn: (LastSubmittedOn || "").startsWith("0001")
                  ? "N/A"
                  : LastSubmittedOn,
                timesheetData: mapResponseDataToUi(Data),
                comments: Comments,
                approverComments: ApproverComments,
                status: Status,
                showCopyButton:
                  !Status || Status === TIMESHEET_ACTION_STATES.DRAFT,
                mode:
                  !Status || Status === TIMESHEET_ACTION_STATES.DRAFT
                    ? "edit"
                    : "read",
                isEditable: Status !== TIMESHEET_ACTION_STATES.APPROVED,
              };
            }
          );
          return formattedProjectList;
        }
        return [];
      } catch (e) {
        console.error("Error fetching the meta information", e);
      }
    },
    [performAction]
  );

  const fetchMetaInfo = useCallback(
    async ({ firstDay, lastDay }) => {
      try {
        const { url: holidaysListURL, options: holidaysOptions } =
          getHolidaysListForCurrentWeekServiceConfig({
            startDate: firstDay,
            endDate: lastDay,
          });
        const { url: projectTasksURL, options: projectTasksOptions } =
          getProjectTaskServiceConfig();
        const currentWeekInfoProjects = await fetchTimesheetDetailsByWeek({
          firstDay,
          lastDay,
        });
        if (currentWeekInfoProjects.length > 0) {
          const formattedProjectList = currentWeekInfoProjects;
          const status = formattedProjectList[0]?.status;
          if (!status || status?.toLowerCase() === "draft") {
            setShowDraftAction(true);
          } else {
            setShowDraftAction(false);
          }
          const submitBtnVisibility = formattedProjectList.some(
            (project) => project.status?.toLowerCase() !== "approved"
          );
          setShowSubmitButton(submitBtnVisibility);
          setProjectsList(formattedProjectList);
          timesheetDetails.current = {};

         const currentstartDate = new Date(firstDay);
         const lastDayBeforeWeek = new Date(currentstartDate);
         lastDayBeforeWeek.setDate(currentstartDate.getDate() - 1);

         // Generate all Mon-Sun week ranges between two dates
         const getWeekRanges = (from, to) => {
           const weeks = [];
           let weekStart = startOfWeek(new Date(from), { weekStartsOn: 1 });
           while (weekStart <= to) {
             const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
             weeks.push({ firstDay: weekStart, lastDay: weekEnd > to ? to : weekEnd });
             weekStart = addDays(weekStart, 7);
           }
           return weeks;
         };

         // For each project: call fetchTimesheetDetailsByWeek for every week from poStartDate to day before current week
         const projectsWithPOStart = currentWeekInfoProjects.filter(p => p.poStartDate);
         const pastTimesheetChecks = await Promise.all(
           projectsWithPOStart.map(async (project) => {
           var threemonthsBefore = subWeeks(new Date(firstDay), 12);
            console.log("three months before date:", threemonthsBefore);
            console.log("project po start date:", new Date(project.poStartDate));
     
             var checkstartDate = project.poStartDate && new Date(project.poStartDate) > threemonthsBefore ? new Date(project.poStartDate) : threemonthsBefore;
         
              console.log("check start date:", checkstartDate);   
              const poEnd = project.poEndDate ? new Date(project.poEndDate) : null;
              const checkEndDate = poEnd && poEnd < lastDayBeforeWeek ? poEnd : lastDayBeforeWeek;
              if (checkstartDate > checkEndDate) return { project, missingWeeks: [] };
              
              const weekRanges = getWeekRanges(checkstartDate, checkEndDate);
              console.log("week ranges to check for project", project.projectName, weekRanges);
              
             const weekResults = await Promise.all(
               weekRanges.map(async (range) => {
                 const result = await fetchTimesheetDetailsByWeek(range);
                 return { range, result };
               })
             );
             const missingWeeks = weekResults
               .filter(({ result }) => {
                 if (!result || result.length === 0) return true;
                 const match = result.find((p) => p.projectId === project.projectId);
                 return !match || !Array.isArray(match.timesheetData) || match.timesheetData.length === 0;
               })
               .map(({ range }) =>
                 `${new Date(range.firstDay).toLocaleDateString()}`
               //  `${new Date(range.firstDay).toLocaleDateString()}
               );
             return { project, missingWeeks };
           })
         );
         // Warn with the specific missing weeks per project
         const missingProjects = pastTimesheetChecks.filter(({ missingWeeks }) => missingWeeks.length > 0);
         if (missingProjects.length > 0) {
           const details = missingProjects
             .map(({ project, missingWeeks }) =>
               `${project.projectName}: Weeks Starting ${missingWeeks.join(" | ")}`
             )
             .join("\n");
           setToastInfo({
             message: `Timesheet missing for the following:\n${details}`,
             type: "warning",
           });
         }
        } else {
          setToastInfo({
            message: translate("labels.noAssociatedProjects"),
            type: "warning",
          });
         
        }
        const dependentRequests = [];
        if (
          Array.isArray(projectTasksCache.current) &&
          projectTasksCache.current.length
        ) {
          dependentRequests.push(
            new Promise((_, reject) => {
              reject("Tasks fetched already");
            })
          );
        } else {
          dependentRequests.push(
            performAction(projectTasksURL, projectTasksOptions)
          );
        }
        dependentRequests.push(performAction(holidaysListURL, holidaysOptions));
        const dependentResponses = await Promise.allSettled(dependentRequests);
        const [projectTasksPromise, holidayListPromise] = dependentResponses;
        if (
          projectTasksPromise.status === "fulfilled" &&
          Array.isArray(projectTasksPromise.value?.Result)
        ) {
          const filteredProjects = projectTasksPromise.value.Result.filter(
            (task) => task.IsActive
          );
          const formattedProjectTasks = filteredProjects.map((task) => {
            const { ProjectTaskID, ProjectTaskName } = task;
            return {
              label: ProjectTaskName,
              value: ProjectTaskID,
              option: {
                taskId: ProjectTaskID,
                taskName: ProjectTaskName,
              },
            };
          });
          setProjectTasksList(formattedProjectTasks);
          projectTasksCache.current = formattedProjectTasks;
        }
        if (
          holidayListPromise.status === "fulfilled" &&
          Array.isArray(holidayListPromise.value?.Result)
        ) {
          const formattedHolidays = holidayListPromise.value?.Result.map(
            (holiday) => {
              const { HolidayDate, HolidayName } = holiday;
              return {
                holidayDate: HolidayDate,
                holidayName: HolidayName,
              };
            }
          );
          const holidayListForWeek = formattedHolidays.map((holiday) =>
            getComparator(holiday.holidayDate)
          );
          setHolidaysList(formattedHolidays);
          setCurrentWeekHolidays(holidayListForWeek);
        }
      } catch (e) {
        console.error("Error fetching the meta information", e);
      }
    },
    [performAction, fetchTimesheetDetailsByWeek]
  );
  // const syncTimesheetDetails = useCallback((projectId, data) => {
  //   const prevInfo = timesheetDetails.current[projectId];
  //   let timesheetId = null;
  //   let lastSubmitted = null;
  //   let comments = "";
  //   if (prevInfo) {
  //     timesheetId = prevInfo.timesheetId;
  //     lastSubmitted = prevInfo.lastSubmittedOn;
  //   }
  //   if (data.comments == undefined) {
  //     comments = prevInfo?.comments;
  //   } else {
  //     comments = data?.comments;
  //   }
  //   timesheetDetails.current[projectId] = {
  //     ...data,
  //     comments: comments,
  //     timesheetId: timesheetId || data.timesheetId,
  //     lastSubmittedOn: lastSubmitted || data.lastSubmittedOn,
  //   };
  // }, []);

  const syncTimesheetDetails = useCallback((projectId, data) => {
    const prevInfo = timesheetDetails.current[projectId] || {};

    timesheetDetails.current[projectId] = {
      ...data,
      comments: data.comments ?? prevInfo.comments ?? "",
      timesheetId: data.timesheetId || prevInfo.timesheetId || null,
      lastSubmittedOn: data.lastSubmittedOn || prevInfo.lastSubmittedOn || null,
    };
  }, []);

  const handleTimesheetModeChange = useCallback((projectId, mode) => {
    setProjectsList((prev) => {
      const updatedProjects = [...prev];
      const projectIdx = updatedProjects.findIndex(
        (project) => project.projectId === projectId
      );
      if (projectIdx > -1) {
        updatedProjects[projectIdx].mode = mode;
        if (mode === "edit") {
          updatedProjects[projectIdx].showEditSwitch = true;
        } else {
          updatedProjects[projectIdx].showEditSwitch = false;
        }
      }
      return updatedProjects;
    });
  }, []);
  const onDeleteAttachment = useCallback(async (timesheetId, fileName) => {
    try {
      const { url, options } = deleteTimesheetAttachmentConfig(
        timesheetId,
        fileName
      );
      const response = await performAction(url, options);
      if (response.StatusCode === 200) {
        setToastInfo({
          message: `${response.Message}`,
          autoExpireToast: true,
          type: "success",
        });
        return true;
      }
    } catch (e) {
      console.error("Error submitting timesheets");
    } finally {
      window.scrollTo({ top: 0, behavior: "smooth" });
      updateCount();
    }
  });
  const handleTimesheetSubmitActions = useCallback(
    async (action) => {
      try {
        var entriesArr = projectsList
          .filter((project) => project.mode === "edit")
          .map((project) => {
            const refData = timesheetDetails.current[project.projectId] || {};
            const timeEntryDetails = unWindData(refData.tasks || []);
            return {
              status: action === "Draft" ? "Draft" : "Submitted",
              projectId: project.projectId,
              projectName: project.projectName,
              timesheetId: refData.timesheetId || project.timesheetId,
              comments: refData.comments,
              timeEntryDetails,
              attachments: refData.attachments,
            };
          });
        if (entriesArr.length === 0) {
          setToastInfo({
            message: translate("labels.notChangesToSave"),
            type: "info",
            autoExpireToast: true,
          });
          return;
        }

        entriesArr = entriesArr.map((entry) => {
          const cleanedAttachments = entry.attachments
            .filter(
              (a) => a.file !== null // && typeof a.file.orignalFile !== "string"
            )
            .map((a) => a.file.orignalFile);
          const res = cleanedAttachments?.length
            ? cleanedAttachments?.filter((item) => item !== undefined)
            : [];
          return {
            ...entry,
            attachments: res,
          };
        });

        const vacationDays = weekDaysArr.filter(
          (week) => week.isHoliday || week.isFutureDate
        ).length;
        const totalWeekDays = weekDaysArr.length;
        const requiredDays = totalWeekDays - vacationDays;
        const incompleteInfoProjects = entriesArr
          .filter((entry) =>
            entry.timeEntryDetails.some((tmpEntry) => tmpEntry.isInCompleteRow)
          )
          .map((entry) => entry.projectId);
        const missingTimeEntriesProjects = entriesArr
          .filter(
            (entry) =>
              entry.timeEntryDetails.filter((tEn) => tEn.hours !== null)
                .length < requiredDays
          )
          .map((entry) => entry.projectId);
        const hasAtleastOneTimeEntry = entriesArr.some((entry) =>
          entry.timeEntryDetails.some((tEntry) => tEntry.hours !== null)
        );
        const noDetailsProjects = entriesArr
          .filter((entry) =>
            entry.timeEntryDetails.every(
              (tEn) => !tEn.taskId && tEn.hours === 0
            )
          )
          .map((entry) => entry.projectId);
        const filteredNoInfoProjects = incompleteInfoProjects.filter(
          (project) => !noDetailsProjects.includes(project)
        );
        if (filteredNoInfoProjects.length > 0) {
          setToastInfo({
            message: `Unable to proceed with ${action} action Projects:${incompleteInfoProjects.join(
              ","
            )} has missing Project task info`,
            type: "info",
            autoExpireToast: true,
          });
          return;
        }
        if (action !== "Draft" && missingTimeEntriesProjects.length > 0) {
          setToastInfo({
            message: `Unable to proceed with submit action Projects:${missingTimeEntriesProjects.join(
              ","
            )} has missing time entries`,
            type: "error",
            autoExpireToast: true,
          });
          return;
        }
        const projectsToBeSubmitted = entriesArr
          .filter(
            (entry) =>
              entry.timeEntryDetails.filter((tEntry) => tEntry.taskId).length >
              0
          )
          .filter((entry) => !noDetailsProjects.includes(entry.projectId));
        if (projectsToBeSubmitted.length === 0 || !hasAtleastOneTimeEntry) {
          setToastInfo({
            message: `Please enter time details to proceed with ${action}`,
            type: "error",
            autoExpireToast: true,
          });
          return;
        }

        // debugger;

        // const validateAttachments = (data) => {
        //   return data.every((item) => {
        //     if (item.status === "Draft") return true;

        //     // Check if at least one file is not null
        //     return (
        //       Array.isArray(item.attachments) &&
        //       item.attachments.some((att) => att.file !== null)
        //     );
        //   });
        // };

        // const isValid = validateAttachments(entriesArr);
        // if (!isValid) {
        //   setToastInfo({
        //     message: `Please upload attachment`,
        //     type: "error",
        //     autoExpireToast: true,
        //   });
        //   return;
        // }

        const validateAttachments = (data) => {
          const invalidProjects = [];

          data.forEach((item) => {
            if (item.status === "Draft") return;

            const totalHours = item.timeEntryDetails.reduce(
              (sum, detail) => sum + (Number(detail.hours) || 0),
              0
            );

            const hasAttachment = item.attachments.length;
            // && item.attachments.some((att) => att?.file != null);

            if (totalHours > 0 && hasAttachment === 0) {
              invalidProjects.push(item.projectName || item.projectId);
            }
          });

          return invalidProjects;
        };

        // Usage
        const invalidProjects = validateAttachments(entriesArr);
        if (invalidProjects.length > 0) {
          setToastInfo({
            message: `Please upload attachments for project(s): ${invalidProjects.join(
              ", "
            )}`,
            type: "error",
            autoExpireToast: true,
          });
          return;
        }

        const reqDetails = projectsToBeSubmitted.map((project) => {
          const timsheetReqDetails = project.timesheetId
            ? updateTimeSheetRequestConfig(project)
            : saveOrSubmitTimeSheetRequestConfig(project);
          return timsheetReqDetails;
        });
        const responsePromises = await Promise.allSettled(
          reqDetails.map((req) => {
            return performAction(req.url, req.options);
          })
        );
        const successFulIds = [];
        const failedIds = [];
        responsePromises.forEach((response, idx) => {
          if (
            response.status === "fulfilled" &&
            response.value.StatusCode === 200
          ) {
            const refProject = projectsToBeSubmitted[idx];
            if (refProject) {
              successFulIds.push({
                projectId: refProject.projectId,
                projectName: refProject.projectName,
                timesheetId:
                  refProject.timesheetId ?? response.value.TimesheetID,
              });
            }
          } else {
            failedIds.push(projectsToBeSubmitted[idx]?.projectName);
          }
        });
        if (successFulIds.length > 0) {
          successFulIds.forEach((info) => {
            const refTimeData = timesheetDetails.current[info.projectId];
            if (refTimeData) {
              timesheetDetails.current[info.projectId] = JSON.parse(
                JSON.stringify({
                  ...refTimeData,
                  timesheetId: refTimeData.timesheetId ?? info.timesheetId,
                  lastSubmittedOn: action !== "Draft" ? new Date() : null,
                })
              );
            }
          });
        }
        const successFullProjectNames = successFulIds
          .map((info) => info.projectName)
          .join(",");
        if (successFulIds.length > 0 && failedIds.length === 0) {
          const msg =
            action === TIMESHEET_ACTION_STATES.DRAFT
              ? translate("labels.timesheetDraftToast")
              : translate("labels.timesheetSubmitToast");
          setToastInfo({
            message: `${msg} ${successFullProjectNames}`,
            autoExpireToast: true,
            type: "success",
          });
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else if (successFulIds.length > 0 && failedIds.length > 0) {
          setToastInfo({
            message: `Timesheet Successfully submitted for projects  ${successFullProjectNames}. However, it failed for projects: ${failedIds.join(
              ", "
            )}.`,
            autoExpireToast: true,
            type: "warning",
          });
        } else {
          setToastInfo({
            message: `Action: ${action} could not be completed for the following projects: ${failedIds.join(
              ", "
            )}.`,
            autoExpireToast: true,
            type: "error",
          });
        }
      } catch (e) {
        console.error("Error submitting timesheets");
      } finally {
        window.scrollTo({ top: 0, behavior: "smooth" });
        updateCount();
      }
    },
    [projectsList, weekDaysArr, performAction]
  );
  const onCopyPreviousWeekTimesheet = useCallback(
    async (projectId, week) => {
      try {
        let localDate = new Date(week.firstDay);
        localDate = subWeeks(localDate, 1);
        const firstDay = startOfWeek(localDate, { weekStartsOn: 1 });
        const lastDay = endOfWeek(localDate, { weekStartsOn: 1 });
        const prevWeekEntries = await fetchTimesheetDetailsByWeek({
          firstDay,
          lastDay,
        });
        if (prevWeekEntries?.length) {
          const projectDataTobeCopied = prevWeekEntries.find(
            (project) => project.projectId === projectId
          );
          if (
            Array.isArray(projectDataTobeCopied?.timesheetData) &&
            projectDataTobeCopied.timesheetData.length > 0
          ) {
            const updatedEntries = projectDataTobeCopied.timesheetData.map(
              (entry) => {
                return {
                  ...entry,
                  hours: entry.hours.map((hour) => {
                    const updatedDay = addDays(new Date(hour.day), 7);
                    return {
                      ...hour,
                      day: updatedDay,
                      comparator: getComparator(updatedDay),
                    };
                  }),
                };
              }
            );
            return updatedEntries;
          }
          return [];
        } else {
          return [];
        }
      } catch (e) {
        console.error("Errror fetching the timesheet details");
      }
    },
    [fetchTimesheetDetailsByWeek]
  );
  const getRefProjectData = (projectId) => {
    const refProject = timesheetDetails.current[projectId];
    if (Array.isArray(refProject?.tasks)) {
      const unwindedData = unWindData(refProject.tasks || []);
      const transformedTimeEntries = unwindedData.map((tEntry) => {
        return {
          ProjectTaskID: tEntry.taskId,
          TaskType: tEntry.taskType,
          PayCode: tEntry.payCode,
          Hours: tEntry.payloadHours,
          Date: tEntry.date,
          ProjectTaskName: tEntry.taskName,
        };
      });
      const payloadEntries = getFormattedTimeEntriesData(
        transformedTimeEntries
      );
      return formatTimeEntriesForReports(payloadEntries);
    }
    return [];
  };
  const formattedReportsData = () => {
    const dataToBeDownloaded = projectsList.map((project) => {
      return {
        ...project,
        lastSubmittedOn:
          timesheetDetails.current[project.projectId]?.lastSubmittedOn ||
          project.lastSubmittedOn,
        timesheetData:
          project.mode === "read"
            ? project.timesheetData
            : getRefProjectData(project.projectId),
      };
    });
    return dataToBeDownloaded;
  };
  const generateTimesheetReport = (format) => {
    const dataToBeGenerated = formattedReportsData();
    if (format === "excel") {
      generateTimesheetExcel(dataToBeGenerated);
    } else {
      exportTimesheetToPDF(dataToBeGenerated);
    }
  };
  const handleToastClose = useCallback(() => {
    setToastInfo(null);
  }, []);
  useEffect(() => {
    if (selectedWeek) {
      fetchMetaInfo(selectedWeek);
    }
  }, [fetchMetaInfo, selectedWeek]);
  useEffect(() => {
    let toastTimer = null;

    if (autoExpireToast) {
      toastTimer = setTimeout(() => {
        setToastInfo(null);
      }, TOAST_TIMER);
    }
    return () => {
      clearTimeout(toastTimer);
    };
  }, [autoExpireToast]);
  useEffect(() => {
    if (toastInfo) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [toastInfo]);
  return {
    error: isError,
    isLoading,
    projectsList,
    holidaysList,
    projectTasksList,
    syncTimesheetDetails,
    handleTimesheetSubmitActions,
    toastInfo,
    currentWeekHolidays,
    weekDaysArr,
    handleWeekChange,
    selectedWeek,
    showDraftAction,
    showSubmitButton,
    handleTimesheetModeChange,
    onCopyPreviousWeekTimesheet,
    generateTimesheetReport,
    handleToastClose,
    hideServiceError,
    timesheetInfo: timesheetDetails.current,
    onDeleteAttachment,
  };
};
